import { useEffect, useRef, useState } from "react";

import {
	Audio,
	AudioMuteButton,
	AudioPlayButton,
	AudioTimeDisplay,
	AudioVolumeSlider,
	AudioWaveform,
	useAudioContext,
} from "@gfazioli/mantine-audio";
import { ActionIcon, Box, Group } from "@mantine/core";
import {
	IconDownload,
	IconPlayerStopFilled,
	IconRepeat,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { useAudioPlayer } from "@/audio/AudioPlayerProvider";
import classes from "./MiniPlayer.module.css";

// Bridges the mantine-audio context (owned by <Audio>) to the global
// AudioPlayerProvider: registers the imperative handle used by
// play-same-id-resume / pause / stop, and mirrors the playing state.
function HandleBridge() {
	const player = useAudioPlayer();
	const audio = useAudioContext();

	const audioRef = useRef(audio);
	audioRef.current = audio;

	const { registerHandle, setIsPlaying } = player;

	useEffect(() => {
		registerHandle({
			play: () => void audioRef.current.play(),
			pause: () => audioRef.current.pause(),
			isPaused: () => audioRef.current.paused,
		});
		return () => registerHandle(null);
	}, [registerHandle]);

	useEffect(() => {
		setIsPlaying(audio.playing);
	}, [audio.playing, setIsPlaying]);

	return null;
}

// Sticky bottom mini-player, rendered app-wide when a file is loaded.
// Markers/boundaries only exist in the standalone player.
export default function MiniPlayer() {
	const { t } = useTranslation();
	const player = useAudioPlayer();
	const [looping, setLooping] = useState(false);

	if (!player.file || player.id === null) {
		return null;
	}

	const file = player.file;

	return (
		<Box
			data-testid="mini-player"
			style={{
				position: "sticky",
				bottom: 0,
				zIndex: 100,
				// Start after the AppShell navbar (0 when collapsed or on
				// chrome-less pages) instead of sliding under it.
				marginInlineStart: "var(--app-shell-navbar-offset, 0px)",
				background: "var(--hooky-surface)",
				borderTop: "1px solid var(--hooky-border)",
				padding: "0.5rem 1rem",
				fontFamily: "Figtree, sans-serif",
			}}
		>
			<Audio
				key={player.id}
				src={file}
				autoPlay
				loop={looping}
				size="sm"
				variant="minimal"
			>
				<HandleBridge />
				<Group gap="md" wrap="nowrap">
					<AudioPlayButton />
					<ActionIcon
						variant="subtle"
						aria-label={t("Stop")}
						onClick={() => player.stop()}
					>
						<IconPlayerStopFilled size={18} />
					</ActionIcon>
					<Box
						className={classes.marqueeWrapper}
						fz="sm"
						fw={700}
						title={player.title ?? undefined}
					>
						<span className={classes.marqueeText}>
							{player.title}
						</span>
					</Box>
					<Box style={{ flex: 1 }}>
						<AudioWaveform height={32} interactive />
					</Box>
					<AudioTimeDisplay />
					<ActionIcon
						variant={looping ? "filled" : "subtle"}
						color={looping ? "hooky" : "gray"}
						aria-label={t("Loop")}
						onClick={() => setLooping((prev) => !prev)}
					>
						<IconRepeat size={18} />
					</ActionIcon>
					<AudioMuteButton />
					<AudioVolumeSlider style={{ width: 90 }} />
					<ActionIcon
						variant="subtle"
						aria-label={t("Download")}
						onClick={() => window.open(file, "download")}
					>
						<IconDownload size={18} />
					</ActionIcon>
				</Group>
			</Audio>
		</Box>
	);
}
